import { useEffect, useRef, type RefObject } from 'react';
import {
	fireUIAnalytics,
	fireTrackAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import { fg } from '@atlassian/jira-feature-gating';
import { useTrigger } from '@atlassian/post-office-context';
import { useHelpPanelLink } from '@atlassian/jira-help-panel-button';
import {
	GRID_NAVIGATION_ONBOARDING_TRIGGERS,
	KEYBOARD_SHORTCUTS_HELP_ARTICLE_ID,
} from '../common/constants.tsx';

export const useKeyboardNavigation = (ref: RefObject<HTMLElement | null>) => {
	const lastFocusedElement = useRef<HTMLElement | null | undefined>();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Post-office trigger handlers for grid navigation onboarding
	const { isActive: isMoreShortcutsClicked } = useTrigger(
		GRID_NAVIGATION_ONBOARDING_TRIGGERS.MORE_SHORTCUTS_CLICKED,
	);
	const { isActive: isOkClicked } = useTrigger(GRID_NAVIGATION_ONBOARDING_TRIGGERS.OK_CLICKED);
	const openHelpPanelLink = useHelpPanelLink();

	useEffect(() => {
		const table = ref.current;
		if (!table) return;

		const getCell = (ele: Element | null | undefined) => ele?.closest('td') ?? ele?.closest('th');
		const isCell = (ele: Element | null | undefined) => getCell(ele) === ele;
		const getFocusableElements = () =>
			Array.from(
				// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage, @atlaskit/platform/no-direct-document-usage
				document.querySelectorAll(
					[
						'a[href]:not([tabindex="-1"]):not([href=""]):not([href="javascript:;"])',
						'button:not([tabindex="-1"])',
						'input:not([tabindex="-1"], [type="hidden"])',
						'select:not([tabindex="-1"])',
						'textarea:not([tabindex="-1"])',
						"[tabindex]:not([tabindex='-1'])",
					].join(','),
				),
			);

		const getCellDepth = (ele: Element | null | undefined, count = 0): number => {
			if (ele instanceof HTMLTableCellElement || !ele?.parentElement) return count;
			return getCellDepth(ele?.parentElement, count + 1);
		};

		const getScrollableContainer = (
			node: HTMLElement | Node | null = table,
		): HTMLElement | null => {
			// Goes up the DOM tree until we encounter a scrollable element.
			if (!(node instanceof HTMLElement)) return null;
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const styles = window.getComputedStyle(node);
			if (styles?.overflowY === 'scroll' || styles?.overflowY === 'auto') return node;
			return getScrollableContainer(node.parentElement);
		};

		const onKeyDown = (event: KeyboardEvent) => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage, @atlaskit/platform/no-direct-document-usage
			const activeElement = document.activeElement;
			const currentTr = activeElement?.closest('tr');

			if (event.key === 'Escape') {
				event.preventDefault();
				const cell = getCell(activeElement);
				const cellButton = cell?.querySelector('button[aria-hidden="false"]');
				if (cellButton instanceof HTMLElement && getCellDepth(cellButton) < 4) {
					return cellButton.focus();
				}
				return cell?.focus();
			}

			if (event.key === 'Tab' && event.shiftKey && activeElement instanceof Element) {
				/*
					If focus would move from our cell to another cell when pressing shift+tab;
					Prevent default and focus last element before the table.
				*/
				const isCurrentCellFocused = isCell(activeElement);
				const index = activeElement.getAttribute('tabindex');
				if (isCurrentCellFocused) activeElement.setAttribute('tabindex', '0');
				const focusable = getFocusableElements();
				const currentFocusableElementsCell = getCell(activeElement);
				const nextFocusableElementsCell = getCell(focusable[focusable.indexOf(activeElement) - 1]);
				if (isCurrentCellFocused) activeElement.setAttribute('tabindex', index ?? '-1');

				if (!isCurrentCellFocused) {
					// go back to grid navigation
					event.preventDefault();
					return currentFocusableElementsCell?.focus();
				}
				if (
					currentFocusableElementsCell === nextFocusableElementsCell ||
					!nextFocusableElementsCell
				) {
					return;
				}

				lastFocusedElement.current = getCell(activeElement);

				// Find the last element before the table in the document
				const lastElementBeforeTable = focusable
					.slice(
						0,
						focusable.findIndex((ele) => table.contains(ele)),
					)
					.at(-1);

				event.preventDefault();
				if (lastElementBeforeTable instanceof HTMLElement) {
					lastElementBeforeTable.focus();
				}
				return;
			}

			if (event.key === 'Tab' && activeElement) {
				/*
					If focus would move from our cell to another cell when pressing tab;
					Prevent default and focus first interactable thing outside the table. (probably create button but not always)
					If we're inside a dialog; don't programmatically control focus.
				*/
				if (activeElement.closest('[role="dialog"]')) return;

				const isCurrentCellFocused = isCell(activeElement);
				const index = activeElement.getAttribute('tabindex');
				if (isCurrentCellFocused) activeElement.setAttribute('tabindex', '0');
				const focusable = getFocusableElements();
				const currentFocusableElementsCell = getCell(activeElement);
				const nextFocusableElementsCell = getCell(focusable[focusable.indexOf(activeElement) + 1]);
				if (isCurrentCellFocused) activeElement.setAttribute('tabindex', index ?? '-1');

				if (
					currentFocusableElementsCell === nextFocusableElementsCell ||
					!nextFocusableElementsCell
				)
					return;
				const nextFocusableElementOutsideTable = focusable
					.slice(focusable.indexOf(activeElement))
					.find((ele): ele is HTMLElement => !table.contains(ele));

				if (!nextFocusableElementOutsideTable) {
					const lastInsideTable = focusable.slice(focusable.indexOf(activeElement)).at(-1);
					if (!(lastInsideTable instanceof HTMLElement)) return;
					lastInsideTable.focus({ preventScroll: true });
					return;
				}
				event.preventDefault();
				nextFocusableElementOutsideTable?.focus();
				return;
			}
			if (!activeElement || !(activeElement instanceof HTMLElement)) return;
			if (!currentTr || !(currentTr instanceof HTMLTableRowElement)) return;
			const currentTd = getCell(activeElement);
			if (!currentTd) return;

			const currentTds = Array.from(currentTr.children).filter((n) => {
				return n.getAttribute('aria-hidden') !== 'true';
			});
			const currentTdIndex = currentTds.indexOf(currentTd);
			const rows = Array.from(table.querySelectorAll('tr:not([aria-hidden="true"])'));
			const currentTrIndex = rows.indexOf(currentTr);

			const isKeyboardNavigableElement =
				activeElement instanceof HTMLTableCellElement ||
				(activeElement instanceof HTMLButtonElement &&
					activeElement.getAttribute('aria-hidden') === 'false');
			if (!isKeyboardNavigableElement) return;
			const next = (() => {
				switch (event.code) {
					case 'ArrowUp':
					case 'KeyJ': {
						fireUIAnalytics(createAnalyticsEvent({}), 'issueTable navigated', 'navigateUp', {
							keyPressed: event.key,
						});
						// Firing track metric to measure nin_grid_navigation_experiment, clean up when cleaning up experiment
						fireTrackAnalytics(
							createAnalyticsEvent({}),
							'keyboardShortcut pressed',
							'KeyboardShortcut',
							{
								action: 'pressed',
								key: 'arrowup',
							},
						);
						if (currentTrIndex === 0) return;
						/* same index, in next row or first index in next row [grouped] */
						const children = Array.from(rows[currentTrIndex - 1]?.children ?? []).filter((ele) => {
							return ele.getAttribute('aria-hidden') !== 'true';
						});
						return children[currentTdIndex] ?? rows[currentTrIndex - 1].children[0];
					}
					case 'ArrowDown':
					case 'KeyK': {
						fireUIAnalytics(createAnalyticsEvent({}), 'issueTable navigated', 'navigateDown', {
							keyPressed: event.key,
						});
						// Firing track metric to measure nin_grid_navigation_experiment, clean up when cleaning up experiment
						fireTrackAnalytics(
							createAnalyticsEvent({}),
							'keyboardShortcut pressed',
							'KeyboardShortcut',
							{
								action: 'pressed',
								key: 'arrowdown',
							},
						);
						if (currentTrIndex === rows.length - 1) return;
						/* same index, in next row or first index in next row [grouped] */
						const children = Array.from(rows[currentTrIndex + 1]?.children ?? []).filter((ele) => {
							return ele.getAttribute('aria-hidden') !== 'true';
						});
						return children[currentTdIndex] ?? rows[currentTrIndex + 1].children[0];
					}
					case 'ArrowLeft':
						fireUIAnalytics(createAnalyticsEvent({}), 'issueTable navigated', 'navigateLeft', {
							keyPressed: event.key,
						});
						return currentTds[currentTdIndex - 1];
					case 'ArrowRight':
						fireUIAnalytics(createAnalyticsEvent({}), 'issueTable navigated', 'navigateRight', {
							keyPressed: event.key,
						});
						return currentTds[currentTdIndex + 1];
					case 'End':
						fireUIAnalytics(createAnalyticsEvent({}), 'issueTable navigated', 'navigateEnd', {
							keyPressed: event.key,
						});
						return currentTds[currentTds.length - 1];
					case 'Home':
						fireUIAnalytics(createAnalyticsEvent({}), 'issueTable navigated', 'navigateHome', {
							keyPressed: event.key,
						});
						return currentTds[0];
					case 'Enter':
					case 'Space': {
						fireUIAnalytics(createAnalyticsEvent({}), 'issueTable navigated', 'navigateEdit', {
							keyPressed: event.key,
						});
						if (activeElement instanceof HTMLTableCellElement) {
							if (event.code === 'Space') event.preventDefault();
							if (currentTd.children.length !== 1) {
								return currentTd.click();
							}
							const interactiveElements = Array.from(
								currentTd.querySelectorAll(
									'button:not([aria-hidden="true"]), a:not([aria-hidden="true"]), input:not([aria-hidden="true"]), select:not([aria-hidden="true"]), textarea:not([aria-hidden="true"]), [role="button"]:not([aria-hidden="true"])',
								),
							);

							if (interactiveElements.length === 1) {
								const element = interactiveElements[0];
								if (element instanceof HTMLElement) {
									element.click();
									return;
								}
							}
						}
						return currentTd.click();
					}
					case 'PageDown': {
						fireUIAnalytics(createAnalyticsEvent({}), 'issueTable navigated', 'navigatePageDown', {
							keyPressed: event.key,
						});

						/* Get last visible index in scrollable area, then focus the cell at the same index */
						const scrollable = getScrollableContainer();
						const rowsReversed = [...rows].reverse();
						if (!scrollable) return null;
						const scrollableRect = scrollable.getBoundingClientRect();
						const lastVisibleIndex =
							rowsReversed.findIndex((row) => {
								const rowRect = row.getBoundingClientRect();
								const isVisible =
									rowRect.bottom > scrollableRect.top && rowRect.top < scrollableRect.bottom;
								return isVisible;
							}) - 1;
						const children = Array.from(
							rowsReversed[Math.max(0, lastVisibleIndex)]?.children ?? [],
						).filter((ele) => {
							return ele.getAttribute('aria-hidden') !== 'true';
						});
						if (children.length === 0) return;

						return (
							children[currentTdIndex] ?? rowsReversed[Math.max(0, lastVisibleIndex)].children[0]
						);
					}
					case 'PageUp': {
						fireUIAnalytics(createAnalyticsEvent({}), 'issueTable navigated', 'navigatePageUp', {
							keyPressed: event.key,
						});
						/* Get first visible index in scrollable area, then focus the cell at the same index */
						const scrollable = getScrollableContainer();
						const rowsExcludingHeader = rows.filter((row) => !row.closest('thead'));
						if (!scrollable) return null;
						const scrollableRect = scrollable.getBoundingClientRect();
						const firstVisibleIndex = rowsExcludingHeader.findIndex((row) => {
							const rowRect = row.getBoundingClientRect();
							const isVisible =
								rowRect.bottom > scrollableRect.top && rowRect.top < scrollableRect.bottom;
							return isVisible;
						});
						const children = Array.from(
							rowsExcludingHeader[Math.max(0, firstVisibleIndex)]?.children ?? [],
						).filter((ele) => {
							return ele.getAttribute('aria-hidden') !== 'true';
						});
						if (children.length === 0) return;
						return (
							children[currentTdIndex] ??
							rowsExcludingHeader[Math.max(0, firstVisibleIndex)].children[0]
						);
					}
					default:
						return null;
				}
			})();

			if (!(next instanceof HTMLElement)) return;
			event.stopImmediatePropagation(); // Stop meatball menu onKeyDown hijacking focus
			event.preventDefault();
			const button = next.querySelector('button[aria-hidden="false"]');
			const depth = getCellDepth(button);
			if (button && button instanceof HTMLElement && depth < 4) {
				return button.focus();
			}
			return next.focus();
		};
		const onFocusIn = (e: FocusEvent) => {
			/*
				Focus on last focused cell if we're moving focus from inside the table to outside the table
				and lastFocusedElement is defined. (tab into table), otherwise focus on the first cell in the table.
			*/
			if (
				e.target instanceof HTMLElement &&
				e.relatedTarget instanceof Element &&
				lastFocusedElement.current
			) {
				if (table.contains(e.target) && !table.contains(e.relatedTarget)) {
					return lastFocusedElement.current.focus();
				}
			}

			/* Focus first element inside table, if we've referred focus from outside table (shift+tab back into table) */
			const target = e.target;
			if (
				!(target instanceof HTMLElement) ||
				!(e.relatedTarget instanceof Element) ||
				table.contains(e.relatedTarget)
			) {
				if (target instanceof HTMLElement) lastFocusedElement.current = getCell(target);
				return;
			}
			const focusable = getFocusableElements();
			const indexOfTable = focusable.findIndex((ele) => table.contains(ele));
			const indexOfRelatedTarget = focusable.indexOf(e.relatedTarget);
			if (indexOfRelatedTarget < indexOfTable) {
				lastFocusedElement.current = undefined;
			}
		};

		const onKeyDownWindow = (e: KeyboardEvent) => {
			/*
				If focus is on the first element outside the table.
				prevent the default behaviour and move focus either to the last focuse element inside the table, or the first table cell.
			*/
			if (e.key === 'Tab' && e.shiftKey) {
				const focusable = getFocusableElements();
				const elementOutsideTable = focusable
					.slice(focusable.findIndex((ele) => table.contains(ele)))
					.find((ele) => !table.contains(ele));
				if (elementOutsideTable === e.target) {
					e.preventDefault();
					if (lastFocusedElement.current && table.contains(lastFocusedElement.current)) {
						return lastFocusedElement.current.focus();
					}
					focusable.find((ele): ele is HTMLElement => table.contains(ele))?.focus();
				}
			}
		};
		table.addEventListener('keydown', onKeyDown);
		table.addEventListener('focusin', onFocusIn);

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.addEventListener('keydown', onKeyDownWindow);
		return () => {
			table.removeEventListener('keydown', onKeyDown);
			table.removeEventListener('focusin', onFocusIn);

			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			window.removeEventListener('keydown', onKeyDownWindow);
		};
		// RefObject is a constant reference.
		// eslint-disable-next-line react-hooks/react-compiler
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ref.current]);

	// Handle "More shortcuts" trigger - open in-product help
	useEffect(() => {
		if (isMoreShortcutsClicked && fg('jira_nin_navigation_onboarding')) {
			openHelpPanelLink(KEYBOARD_SHORTCUTS_HELP_ARTICLE_ID);
		}
	}, [isMoreShortcutsClicked, openHelpPanelLink]);

	// Handle "OK" trigger - focus on first th cell in the table
	useEffect(() => {
		if (isOkClicked && fg('jira_nin_navigation_onboarding')) {
			const table = ref.current;
			if (table) {
				const firstThCell = table.querySelector('th');
				if (firstThCell instanceof HTMLElement) {
					firstThCell.focus();
				}
			}
		}
	}, [isOkClicked, ref]);
};
